import { queryOptions, useSuspenseQuery } from '@tanstack/react-query'

import { marketFetcher, marketFetcherV2 } from '@/libs/fetcher/presets'
import { CoinId, CoinMarket, CoinMeta } from '@/types/coin'

export interface SocialDataResponse {
  reddit?: {
    followers?: number
  }
  twitter?: {
    followers?: number
  }
  telegram?: {
    followers?: number
  }
}

export enum SocialsType {
  All = 'all',
  Twitter = 'twitter',
  Reddit = 'reddit',
  CoinsAI = 'coins-ai',
}

export enum SocialMediaType {
  Video = 'video',
  Articles = 'articles',
}

export type RedditThumbnail = 'image' | 'self' | 'default' | string

export type Reddit = {
  username: string
  create_time: number | string
  url: string

  upvotes: number
  downvotes: number

  title: string

  link?: string
  text?: string
  thumbnail?: {
    url: RedditThumbnail
    width?: number
    height?: number
  }
}

export type RedditWithOriginUrl = Reddit & {
  originUrl: string
}

export type TweetEntity = {
  start: number
  end: number
}

export type TweetMediaEntities = {
  type: 'photo' | 'youtube' | 'vimeo' | 'vine' | 'video' | 'animated_gif'
  url: string
  width?: number
  height?: number
}[]

export type TweetEntities = {
  hashtags?: (TweetEntity & {
    tag: string
  })[]
  cashtags: (TweetEntity & {
    tag: string
  })[]
  urls?: (TweetEntity & {
    url: string
    expanded_url: string
    display_url: string
  })[]
  mentions?: (TweetEntity & {
    username: string
    id: string
  })[]
  medias?: TweetMediaEntities
}
export type Tweet = {
  id: string | number
  username: string
  alias: string
  avatar?: string
  create_time: number | string
  reply_count: number
  text: string
  hashtags?: TweetEntities['hashtags']
  cashtags?: TweetEntities['cashtags']
  urls?: TweetEntities['urls']
  mentions?: TweetEntities['mentions']
  medias?: TweetEntities['medias']
}

export interface SocialNewsResponse {
  reddit_posts: Reddit[]
  tweets: Tweet[]
}

export type Thumbnail = {
  url: string
  height: number
  width: number
}

export interface Video {
  id: string
  title: string
  description: string
  create_time: string
  link: string
  source: string
  thumbnail?: string
  thumbnails?: {
    default: Thumbnail
    medium?: Thumbnail
    high?: Thumbnail
  }
}

export interface Article {
  title: string
  content?: string
  description?: string
  link: string
  pubDate: string
  thumbnail?: string
  source?: string
  publication_time?: string
  thumbnails?: {
    default: Thumbnail
    medium?: Thumbnail
    high?: Thumbnail
  }
  categories?: string[]
  id?: string
}

export interface SocialVideosResponse {
  total: number
  page: number
  size: number
  videos?: Video[]
  articles?: Article[]
}

export type CoinFromAll = Pick<
  CoinMeta,
  'id' | 'name' | 'symbol' | 'slug' | 'source_slug' | 'icon' | 'price_engine_enabled'
> & {
  source_id: CoinId
}

// Global coins extends CoinFromAll with additional fields like rank
export type GlobalCoin = CoinFromAll & Pick<CoinMarket, 'rank'>

export const fetchSocialData = (slug: string) =>
  marketFetcher<SocialDataResponse>(`/token/${slug}/social-metrics`)

export const socialDataQueryOptions = (slug: string) =>
  queryOptions({
    queryKey: ['socialData', slug],
    queryFn: () => fetchSocialData(slug),
  })

export const useSocialData = (slug: string) => useSuspenseQuery(socialDataQueryOptions(slug))

export const fetchSocials = (tokenId: number, type: SocialsType = SocialsType.All) =>
  marketFetcherV2<SocialNewsResponse>(`/token/${tokenId}/social-news?platform=${type}`)

export const socialNewsQueryOptions = (tokenId: number, type: SocialsType = SocialsType.All) =>
  queryOptions({
    queryKey: ['socialNews', tokenId, type],
    queryFn: () => fetchSocials(tokenId, type),
  })

export const useSocialNews = (tokenId: number, type: SocialsType = SocialsType.All) =>
  useSuspenseQuery(socialNewsQueryOptions(tokenId, type))

export const fetchSocialVideos = (
  tokenId: number,
  page: number = 1,
  size: number = 10,
  type: SocialMediaType = SocialMediaType.Video,
) =>
  marketFetcherV2<SocialVideosResponse>(
    `/token/${tokenId}/news?platform=${type}&page=${page}&size=${size}`,
  )

export const socialVideosQueryOptions = (
  tokenId: number,
  page: number = 1,
  size: number = 10,
  type: SocialMediaType = SocialMediaType.Video,
) =>
  queryOptions({
    queryKey: ['socialVideos', tokenId, page, size, type],
    queryFn: () => fetchSocialVideos(tokenId, page, size, type),
  })

export const useSocialVideos = (
  tokenId: number,
  page: number = 1,
  size: number = 10,
  type: SocialMediaType = SocialMediaType.Video,
) => useSuspenseQuery(socialVideosQueryOptions(tokenId, page, size, type))
